'use client';

import { useFieldContext } from '@/lib/form/contexts';

type RadioOption = string | { value: string; label: string };

interface RadioGroupProps {
  options: readonly RadioOption[];
  required?: boolean;
  /**
   * Optional group label/heading, rendered visibly above the options.
   * Unlike `FormInput`/`FormTextarea`'s `sr-only` `label` (which has a
   * visible substitute — the placeholder text inside the input), a radio
   * group has no other visible text conveying what the options are for, so
   * this one renders on-screen rather than `sr-only`. Omitted by existing
   * callers (e.g. `ContactForm.tsx`), which keeps their current behavior
   * unchanged.
   */
  label?: string;
}

export const RadioGroup = ({ options, required, label }: RadioGroupProps) => {
  const field = useFieldContext<string>();
  const error = field.state.meta.errors[0]?.message;
  return (
    <>
      {label && (
        <div className="mb-2 text-xs font-medium md:text-sm">
          {label}
          {required && ' *'}
        </div>
      )}
      <div className="flex gap-4">
        {options.map((opt) => {
          const value = typeof opt === 'string' ? opt : opt.value;
          const label = typeof opt === 'string' ? opt : opt.label;
          return (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 text-xs md:text-sm"
            >
              <input
                type="radio"
                name={field.name}
                value={value}
                checked={field.state.value === value}
                onChange={() => field.handleChange(value)}
                onBlur={field.handleBlur}
                className="accent-primary"
              />
              {label}
              {required && ' *'}
            </label>
          );
        })}
      </div>
      {error && <p className="mt-1 pl-1 text-xs text-red-500">{error}</p>}
    </>
  );
};
